<template lang="pug">
#speaker-page
  spinner(v-if="settings.loading")
  div(v-else)
    .speaker-header(:style="`background-image:url(${backgroundUrl})`")
      .container
        cl-img.avatar(
          v-if="speaker._embedded.profile_image"
          :cl-id="speaker._embedded.profile_image.cl_id"
          :transform="{ width: 250, height: 250, crop: 'thumb' }"
        )
        h1.is-size-1 {{ speaker.full_name }}
        p.collection-desc.is-size-5(v-if="speaker.title") {{ speaker.title }}
    .speaker-body
      .container
        .content(v-if="speaker.bio")
          h3 Biography
          read-more(
            :value="speaker.bio"
            :length="isMobile ? 220 : 0"
          )
        .content-block(v-if="collections.length >= 3")
          .block-header.mobile-padding
            h3.is-size-5 Popular series
            router-link(:to="{ name: 'services', query: { tab: 'series', speakers: speaker.id } }") All series
          .columns.is-multiline
            .column.is-one-third(
              v-for="collection in collections"
              :key="collection.id"
            )
              collection-list-item(
                :value="collection"
                hoverable
              )
        .content-block
          .block-header.mobile-padding
            h3.is-size-5 Latest sermons
            router-link(:to="{ name: 'services', query: { tab: 'sermons', speakers: speaker.id } }") All sermons
          .columns.is-multiline
            .column.is-one-third(
              v-for="episode in episodes"
              :key="episode.id"
            )
              episode-list-item(
                :value="episode"
                hoverable
              )
</template>

<script>
import { mapGetters } from 'vuex';
import cloudinary from 'cloudinary-core';
import moment from 'moment';
import { SEARCH } from '@/utils/http';
import CollectionListItem from '@/components/collections/CollectionListItem.vue';
import EpisodeListItem from '@/components/episodes/EpisodeListItem.vue';
import EpisodesTab from '@/components/episodes/EpisodesTab.vue';
import ReadMore from '@/components/ReadMore.vue';
import Spinner from '@/components/Spinner.vue';
import * as actionTypes from '@/store/types/actions';
import * as getterTypes from '@/store/types/getters';

export default {
  name: 'speaker',
  components: {
    CollectionListItem,
    EpisodeListItem,
    EpisodesTab,
    ReadMore,
    Spinner,
  },
  computed: {
    ...mapGetters({
      account: getterTypes.GET_ACCOUNT,
      speaker: getterTypes.GET_SPEAKER,
    }),
    backgroundUrl() {
      let defaultImage = null;
      // TODO need to put this in a web accessible place
      switch (this.account.abbreviation) {
        case 'CWO': {
          defaultImage = `${window.location.origin}/images/cwo/building.jpg`;
          break;
        }
        default:
      }
      if (!this.speaker._embedded.header_image) return defaultImage;
      let opts = {};
      if (this.isMobile) {
        opts = {
          width: 600,
          height: 600,
          effect: 'blur:700',
          quality: 'auto',
          fetch_format: 'auto',
          crop: 'thumb',
          opacity: 30,
          format: 'png',
        };
      } else {
        opts = {
          width: 2000,
          height: 700,
          effect: 'blur:700',
          quality: 'auto',
          fetch_format: 'auto',
          crop: 'fill',
          opacity: 30,
          format: 'png',
        };
      }
      return this.cloudinary.url(this.speaker._embedded.header_image.cl_id, opts);
    },
    collectionQuery() {
      const payload = {
        size: 3,
        sort: {
          start_date: { order: 'desc' },
          'title.raw': { order: 'asc' },
        },
        query: {
          bool: {
            must: [
              { match: { account_id: this.account.id } },
              { terms: { speaker_ids: [this.$route.params.id] } },
            ],
          },
        },
      };
      return payload;
    },
    episodeQuery() {
      const today = moment().format('YYYY-MM-DD');
      const payload = {
        size: 12,
        sort: {
          pub_date: { order: 'desc' },
          'title.raw': { order: 'asc' },
        },
        query: {
          bool: {
            must: [
              { match: { account_id: this.account.id } },
              { terms: { '_embedded.speaker.id': [this.$route.params.id] } },
              { match: { is_published: true } },
              { range: { pub_date: { lte: today } } },
            ],
          },
        },
      };
      return payload;
    },
    isMobile() {
      return window.innerWidth <= 769;
    },
  },
  async created() {
    this.settings.loading = true;
    this.cloudinary = cloudinary.Cloudinary.new({
      cloud_name: process.env.VUE_APP_CLOUDINARY_CLOUD_NAME,
    });
    if (this.$route.params.id) {
      await Promise.all([
        this.getSpeaker(),
        this.getCollections(),
        this.getEpisodes(),
      ]);
    }
    this.settings.loading = false;
    return Promise.resolve();
  },
  data() {
    return {
      cloudinary: null,
      collections: [],
      episodes: [],
      settings: {
        loading: false,
      },
    };
  },
  methods: {
    async getCollections() {
      const { data } = await SEARCH.post('/doxa_episode_collections/_search', this.collectionQuery);
      this.collections = data.hits.hits.map(h => h._source);
      return data;
    },
    async getEpisodes() {
      const { data } = await SEARCH.post('/doxa_episodes/_search', this.episodeQuery);
      this.episodes = data.hits.hits.map(h => h._source);
      return data;
    },
    async getSpeaker() {
      await this.$store.dispatch(actionTypes.DO_GET_SPEAKER, {
        id: this.$route.params.id,
        zoom: 'header_image,profile_image',
      });
    },
  },
};
</script>

<style lang="scss">
#speaker-page {
  .speaker-header {
    @include hero;
    // background-size: contain;
    .avatar {
      width: 125px;
      height: 125px;
      border-radius: 100px;
      display: inline;
      margin-bottom: 0.75rem;
    }
    h1 {
      margin-bottom: 1rem;
    }
  }
  .speaker-body {
    padding: 3rem 0;
    .content {
      background-color: $white;
      padding: 1.5rem;
    }
    .content-block {
      padding: 1.5rem 0;
      .block-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding-bottom: 1rem;
      }
    }
  }
  @media (max-width: $tablet) {
    .speaker-header {
      background-size: cover;
      background-attachment: inherit;
    }
    .speaker-body {
      padding: 1.75rem 0;
      .content {
        margin: 0 1.25rem;
      }
      .content-block {
        .columns {
          @include carousel;
        }
      }
    }
  }
}
</style>
